import { useId } from 'react';
import { Badge, Box, FileField, InlineStack, Text, Truncated, type BoxProps } from '@customerio/pluma-components/react';
import type { PushProvider } from '../types';
import * as styles from '../styles.css';

const FILE_FIELDS = {
  apns: {
    extension: '.p8',
    label: 'Certificate',
    name: 'certificate_upload',
    preview: `-----BEGIN PRIVATE KEY-----
XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
-----END PRIVATE KEY-----`,
  },
  fcm: {
    extension: '.json',
    label: 'Server key',
    name: 'server_key_upload',
    preview: `{
  "type": "XXXXXX",
  "project_id": "XXXXXX",
  "private_key_id": "XXXXXX",
  "private_key": "XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX`,
  },
} satisfies Record<PushProvider, { extension: string; label: string; name: string; preview: string }>;

export type CredentialFileFieldProps = {
  provider: PushProvider;
  /** The file the user has picked but not yet saved. */
  selectedFileName: string | null;
  /** The file name stored alongside the saved credential. */
  savedFileName: string | null;
  /** Whether a new file may replace the one already there. */
  isEditable: boolean;
  /** Whether the picker is locked — a save in flight, or a viewer who cannot edit. */
  isDisabled: boolean;
  /**
   * Marks the file as the thing a failed save is complaining about. The message
   * itself is shown elsewhere; here it only colours the control.
   */
  isInvalid?: boolean;
  /** Shown under the card rather than under the button, which sits inside it. */
  error?: string;
  onFileSelected: (file: File) => void;
} & BoxProps;

/**
 * The upload slot for one provider's credential file: a masked preview of what
 * the file looks like, and the name of the file currently in place.
 *
 * The contents of a stored credential are never returned by the API, so the
 * preview is fixed sample text rather than the real file.
 */
export function CredentialFileField({
  provider,
  selectedFileName,
  savedFileName,
  isEditable,
  isDisabled,
  isInvalid = false,
  error,
  onFileSelected,
  ...boxProps
}: CredentialFileFieldProps) {
  const errorId = useId();
  const { extension, label, name, preview } = FILE_FIELDS[provider];
  const hasError = isInvalid || error != null;
  const describedBy = error == null ? undefined : errorId;
  const fileName = selectedFileName ?? (savedFileName == null ? null : `${savedFileName} (current)`);
  const fieldLabel = `${label} (${extension})`;

  return (
    <Box {...boxProps}>
      <Text as="div" className={styles.fileLabel}>
        {fieldLabel}
      </Text>

      <Box className={fileName == null ? `${styles.fileCard} ${styles.fileCardEmpty}` : styles.fileCard}>
        <Box className={styles.fileExtension}>{extension}</Box>

        {fileName == null ? (
          <Box className={styles.fileChooseButton}>
            <FileField
              accept={extension}
              ariaLabel={fieldLabel}
              buttonLabel="Choose file…"
              buttonVariant="primary"
              isDisabled={isDisabled || !isEditable}
              isInvalid={hasError}
              ariaDescribedby={describedBy}
              name={name}
              onFileSelected={onFileSelected}
            />
          </Box>
        ) : null}

        <Box as="pre" className={styles.filePreview}>
          {preview}
        </Box>

        {fileName == null ? null : (
          <InlineStack className={styles.fileBar} alignBlock="center" gap="200">
            {/* The name sits on a row as tall as the button beside it, so it
                centres against that rather than against its own line. */}
            <InlineStack className={styles.fileName} alignBlock="center" flexGrow="1" minW="0">
              <Truncated>{fileName}</Truncated>
            </InlineStack>
            {isEditable ? (
              <FileField
                accept={extension}
                ariaLabel={`Upload a new ${label.toLowerCase()}`}
                buttonLabel="Upload new"
                isDisabled={isDisabled}
                isInvalid={hasError}
                ariaDescribedby={describedBy}
                name={name}
                onFileSelected={onFileSelected}
              />
            ) : null}
          </InlineStack>
        )}
      </Box>

      <FileError mt="050" id={errorId} error={error} />
    </Box>
  );
}

type FileErrorProps = {
  id: string;
  error: string | undefined;
} & BoxProps;

/** Set the way a form field sets its own error, under the control. */
function FileError({ id, error, ...boxProps }: FileErrorProps) {
  if (error == null) {
    return null;
  }

  return (
    <Box {...boxProps}>
      <Badge color="critical" bold={false} id={id}>
        {error}
      </Badge>
    </Box>
  );
}
